import { useState } from 'react';
import styled from 'styled-components';
import GenericPopup from '../../../components/GenericPopup';
import { useCart } from '../../contexts/CartContext';

const PopupTitle = styled.h3`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  text-align: center;
`;

const ItemDetails = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ItemName = styled.h4`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ItemDescription = styled.p`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.7;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const StockStatus = styled.div`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.md};
  background: ${props => props.$inStock ? props.theme.colors.accent.bright : props.theme.colors.neutral.light};
  color: ${props => props.$inStock ? props.theme.colors.accent.main : props.theme.colors.primary.text};
`;

const OptionsSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const OptionGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const OptionLabel = styled.label`
  display: block;
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.theme.colors.primary.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
`;

const QuantityButton = styled.button`
  background: ${props => props.theme.colors.primary.background};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.primary.buttons};
  font-size: 1.2rem;
  font-weight: 600;
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: ${props => props.theme.colors.primary.background};
      color: ${props => props.theme.colors.primary.buttons};
    }
  }
`;

const QuantityDisplay = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  min-width: 40px;
  text-align: center;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  accent-color: ${props => props.theme.colors.accent.main};
  width: 18px;
  height: 18px;
`;

const CheckboxLabel = styled.label`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary.text};
  cursor: pointer;
  user-select: none;
`;

const RecurringNote = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.8rem;
  color: ${props => props.theme.colors.accent.main};
  text-align: center;
  margin-top: ${props => props.theme.spacing.xs};
  font-style: italic;
`;

const PriceBreakdown = styled.div`
  background: ${props => props.theme.colors.neutral.light};
  border-radius: 8px;
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xs};
  
  &:last-child {
    margin-bottom: 0;
    padding-top: ${props => props.theme.spacing.xs};
    border-top: 1px solid ${props => props.theme.colors.neutral.medium};
    font-weight: 600;
  }
`;

const PriceLabel = styled.span`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary.text};
`;

const PriceValue = styled.span`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
`;

const ActionButton = styled.button`
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: ${props => props.theme.typography.button.fontSize};
  font-weight: ${props => props.theme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme.typography.button.letterSpacing};
  text-transform: ${props => props.theme.typography.button.textTransform};
  
  background: ${props => props.$primary ? props.theme.colors.primary.buttons : 'transparent'};
  color: ${props => props.$primary ? props.theme.colors.primary.background : props.theme.colors.primary.buttons};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: ${props => props.$primary ? props.theme.colors.primary.buttons : 'transparent'};
      color: ${props => props.$primary ? props.theme.colors.primary.background : props.theme.colors.primary.buttons};
      transform: none;
    }
  }
`;

const AddToCartPopup = ({ isOpen, onClose, item, itemType }) => {
  const [quantity, setQuantity] = useState(1);
  const [isRecurring, setIsRecurring] = useState(false);
  const { addToCart } = useCart();

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const calculatePrice = () => {
    if (!item || !item.price) {
      return {
        basePrice: 0,
        discount: 0,
        total: 0
      };
    }
    const basePrice = item.price * quantity;
    // Only apply discount for non-membership items when recurring is selected
    const discount = (isRecurring && itemType !== 'membership') ? basePrice * 0.1 : 0;
    return {
      basePrice,
      discount,
      total: basePrice - discount
    };
  };

  const handleAddToCart = () => {
    if (!item) return;
    
    if (itemType === 'store' && !item.inStock) return;
    
    const cartId = item.category ? `${itemType}_${item.category}_${item.name}` : `${itemType}_${item.name}`;
    const cartItem = {
      id: cartId,
      name: item.name,
      description: item.description,
      price: item.price,
      quantity,
      isRecurring: itemType === 'membership' ? true : isRecurring, // Memberships are always recurring
      itemType,
      classes: item.classes || null
    };
    
    addToCart(cartItem);
    onClose();
    
    // Reset form
    setQuantity(1);
    setIsRecurring(false);
  };

  if (!item) return null;

  const { basePrice, discount, total } = calculatePrice();

  return (
    <GenericPopup isOpen={isOpen} onClose={onClose} maxWidth="450px" fixedHeight={false}>
      <PopupTitle>Add to Cart</PopupTitle>
      
      <ItemDetails>
        <ItemName>{item.name}</ItemName>
        <ItemDescription>{item.fullDescription}</ItemDescription>
        {itemType === 'store' && !item.inStock && (
          <StockStatus $inStock={item.inStock}>
            Out of Stock
          </StockStatus>
        )}
      </ItemDetails>

      <OptionsSection>
        <OptionGroup>
          <OptionLabel>Quantity</OptionLabel>
          <QuantityContainer>
            <QuantityButton 
              onClick={() => handleQuantityChange(-1)} 
              disabled={quantity <= 1 || (itemType === 'store' && !item.inStock)}
            >
              −
            </QuantityButton>
            <QuantityDisplay>{quantity}</QuantityDisplay>
            <QuantityButton 
              onClick={() => handleQuantityChange(1)}
              disabled={itemType === 'store' && !item.inStock}
            >
              +
            </QuantityButton>
          </QuantityContainer>
        </OptionGroup>

        {itemType !== 'membership' && (
          <OptionGroup>
            <CheckboxContainer>
              <Checkbox 
                id="recurring" 
                checked={isRecurring} 
                onChange={(e) => setIsRecurring(e.target.checked)}
              />
              <CheckboxLabel htmlFor="recurring">
                Monthly Recurring (5% discount)
              </CheckboxLabel>
            </CheckboxContainer>
            {isRecurring && (
              <RecurringNote>
                You can cancel anytime from your account
              </RecurringNote>
            )}
          </OptionGroup>
        )}
      </OptionsSection>

      <PriceBreakdown>
        <PriceRow>
          <PriceLabel>Price ({quantity} {item.classes ? `x ${item.classes} classes` : 'item'})</PriceLabel>
          <PriceValue>${basePrice.toFixed(2)}</PriceValue>
        </PriceRow>
        {discount > 0 && (
          <PriceRow>
            <PriceLabel>Recurring Discount (10%)</PriceLabel>
            <PriceValue>-${discount.toFixed(2)}</PriceValue>
          </PriceRow>
        )}
        <PriceRow>
          <PriceLabel>Total{isRecurring || itemType === 'membership' ? ' (monthly)' : ''}</PriceLabel>
          <PriceValue>${total.toFixed(2)}</PriceValue>
        </PriceRow>
      </PriceBreakdown>

      <ButtonContainer>
        <ActionButton onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton 
          $primary 
          onClick={handleAddToCart}
          disabled={itemType === 'store' && !item.inStock}
        >
          {itemType === 'store' && !item.inStock ? 'Out of Stock' : 'Add to Cart'}
        </ActionButton>
      </ButtonContainer>
    </GenericPopup>
  );
};

export default AddToCartPopup;